import { DrawerProps } from '@mui/material/Drawer'
import { useTranslation } from 'next-i18next'
import * as React from 'react'
import { ReactEventHandler } from 'react'
import { StyledDrawer, StyledMenu } from './styled'

type IFiltersDrawerProps = DrawerProps & {
  title: string
  subtitle: string | React.ReactElement
  trailing?: string | React.ReactElement
  anchorEl: HTMLElement | undefined
  openDrawer: boolean
  openModal: boolean
  onApply: ReactEventHandler
  onClose: ReactEventHandler
  onClear: ReactEventHandler
}

export default function FiltersDrawerMenu({
  children,
  title,
  subtitle,
  trailing,
  anchorEl,
  openDrawer,
  openModal,
  onApply,
  onClose,
  onClear,
  ...DrawerProps
}: IFiltersDrawerProps) {
  const { t } = useTranslation('common')

  const content = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-ink-800">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('A11Y.CLOSE')}
          className="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
        >
          <i className="si si-close text-base text-ink-700" aria-hidden />
        </button>
      </div>

      {/* Price labels */}
      <div className="mt-3 flex items-center justify-between text-sm text-ink-200">
        <span>{subtitle}</span>
        {trailing && <span>{trailing}</span>}
      </div>

      {/* Slider content */}
      <div className="mt-2 w-full max-w-full overflow-hidden">{children}</div>

      {/* Actions */}
      <div className="mt-4 flex items-center justify-between border-t border-ink-100/15 pt-4">
        <button
          type="button"
          className="rounded text-sm font-medium text-ink-700 underline-offset-4 transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          onClick={onClear}
        >
          {t('FILTER.RESET_BUTTON')}
        </button>
        <button
          type="button"
          className="rounded-full bg-blue-500 px-6 py-2 text-sm font-semibold text-white-500 transition-colors hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          onClick={onApply}
        >
          {t('FILTER.APPLY_BUTTON')}
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Mobile drawer */}
      <StyledDrawer {...DrawerProps} open={openDrawer}>
        <div className="flex size-full flex-col bg-white-500 px-5 pt-4 pb-5">
          {content}
        </div>
      </StyledDrawer>

      {/* Desktop popover */}
      <StyledMenu
        anchorEl={anchorEl}
        open={openModal}
        onClose={onClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button'
          }
        }}
      >
        <div className="w-full max-w-[380px] p-5">{content}</div>
      </StyledMenu>
    </>
  )
}
